import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronRight, IconClock } from '@tabler/icons-react';
import {
  Button,
  IconButton,
  InlineError,
  overlayPresenceProps,
  ProximityScope,
  Text,
} from '@wapharma/design-system';
import { StepTravel } from './StepTravel';
import { subscribeOpenLaunch } from '../lib/launchShell';
import {
  LAUNCH_BLOCKING_IDS,
  useLaunchReadiness,
  type LaunchCheckId,
} from '../hooks/useLaunchReadiness';
import { homePreviewUrl, shopPreviewUrl } from '../lib/storefrontPublicUrl';
import { StorefrontSiteSettingsPage } from '../pages/StorefrontSiteSettingsPage';
import { FulfillmentSettingsPage } from '../pages/FulfillmentSettingsPage';
import { DiscountCodesPage } from '../pages/DiscountCodesPage';
import { StorefrontFeaturedProductsPanel } from '../pages/storefront-home/StorefrontFeaturedProductsPanel';
import { CatalogCampaignsTab } from './catalog/CatalogCampaignsTab';
import { LaunchFlowsStep } from './LaunchFlowsStep';
import dockStyles from './OnboardingDock.module.css';
import styles from './SetupWizard.module.css';

const STEPS: { id: LaunchCheckId; label: string }[] = [
  { id: 'legal', label: 'Identité' },
  { id: 'shipping', label: 'Livraison' },
  { id: 'payment', label: 'Paiement' },
  { id: 'featured', label: 'À la une' },
  { id: 'flows', label: 'Parcours' },
  { id: 'campaigns', label: 'Campagnes' },
  { id: 'coupons', label: 'Codes promo' },
];

function stepIndexFromOpts(step?: number): number {
  if (typeof step !== 'number' || !Number.isFinite(step)) return 0;
  return Math.max(0, Math.min(STEPS.length - 1, Math.floor(step)));
}

export function LaunchWizard() {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [onDonePanel, setOnDonePanel] = useState(false);

  const { byId, loading, error, reload, ready, progressPercent } = useLaunchReadiness();

  const openWizard = useCallback((opts?: { step?: number }) => {
    setOpen(true);
    setExpanded(true);
    setOnDonePanel(false);
    setStepIndex(stepIndexFromOpts(opts?.step));
    void reload();
  }, [reload]);

  useEffect(() => subscribeOpenLaunch((opts) => openWizard(opts)), [openWizard]);

  useEffect(() => {
    if (!open || onDonePanel) return;
    void reload();
  }, [open, stepIndex, onDonePanel, reload]);

  const currentStep = STEPS[stepIndex];
  const currentCheck = currentStep ? byId.get(currentStep.id) : undefined;
  const isOptional = currentCheck?.optional === true ||
    (currentStep != null && !LAUNCH_BLOCKING_IDS.includes(currentStep.id));
  const canContinueStep =
    onDonePanel ||
    isOptional ||
    currentCheck?.ok === true;

  const stepDone = useMemo(
    () => STEPS.map((s) => byId.get(s.id)?.ok === true),
    [byId],
  );

  const pct = progressPercent;
  const progressLabel = `${pct}\u00a0%`;
  const headerStepLabel = onDonePanel
    ? 'Terminé'
    : (currentStep?.label ?? STEPS[0]!.label);
  const headerAria = onDonePanel
    ? `Prêt pour le lancement, ${progressLabel}, terminé`
    : `Prêt pour le lancement, ${progressLabel}, étape ${headerStepLabel}`;

  const collapsePanel = () => setExpanded(false);
  const expandPanel = () => setExpanded(true);
  const dismiss = () => {
    setOpen(false);
    setExpanded(false);
    setOnDonePanel(false);
  };

  const goContinue = () => {
    if (!canContinueStep) return;
    if (stepIndex >= STEPS.length - 1) {
      setOnDonePanel(true);
      return;
    }
    setStepIndex((i) => i + 1);
  };

  if (!open || typeof document === 'undefined') return null;

  const sheet = (
    <div className={dockStyles.portalHost} data-wp-setup-wizard="" data-wp-launch-wizard="">
      <div
        className={
          expanded ? `${dockStyles.backdrop} ${dockStyles.backdropVisible}` : dockStyles.backdrop
        }
        aria-hidden
        onMouseDown={(e) => {
          if (!expanded || e.target !== e.currentTarget) return;
          collapsePanel();
        }}
      />
      <aside
        className={
          expanded
            ? `${dockStyles.sheet} ${dockStyles.sheetExpanded}`
            : `${dockStyles.sheet} ${dockStyles.sheetCollapsed}`
        }
        role="dialog"
        aria-modal={expanded ? true : undefined}
        aria-labelledby={titleId}
        aria-expanded={expanded}
        {...overlayPresenceProps(expanded)}
      >
        <div className={styles.stripChrome}>
          <div className={`${dockStyles.strip} ${styles.stripRow}`} data-wp-dock-strip="">
            <button
              type="button"
              className={styles.stripToggle}
              onClick={() => (expanded ? collapsePanel() : expandPanel())}
              aria-label={
                expanded ? `${headerAria}. Réduire.` : `${headerAria}. Ouvrir.`
              }
            >
              <span className={styles.headerLead}>
                <span id={titleId} className={dockStyles.stripCopy}>
                  Prêt pour le lancement
                </span>
                {!expanded ? (
                  <span className={styles.headerStepHint}>{headerStepLabel}</span>
                ) : null}
              </span>
              <span className={dockStyles.stripEnd}>
                <span className={styles.progressPct}>{progressLabel}</span>
                <span
                  className={
                    expanded
                      ? `${dockStyles.pillTrack} ${dockStyles.pillTrackWide}`
                      : dockStyles.pillTrack
                  }
                  aria-hidden
                >
                  <span className={dockStyles.pillFill} style={{ width: `${pct}%` }} />
                </span>
                <IconChevronRight
                  size={18}
                  stroke={1.6}
                  aria-hidden
                  className={
                    expanded
                      ? `${dockStyles.stripChevron} ${dockStyles.stripChevronExpanded}`
                      : dockStyles.stripChevron
                  }
                />
              </span>
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              borderless
              className={styles.stripDismiss}
              aria-label="Fermer"
              title="Fermer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dismiss();
              }}
            >
              <IconClock size={18} stroke={1.6} aria-hidden />
            </IconButton>
          </div>
          {expanded ? (
            <ProximityScope axis="x" className={styles.stepTabs} aria-label="Étapes">
              {STEPS.map((s, i) => {
                const done = stepDone[i];
                const active = !onDonePanel && i === stepIndex;
                const canGo = onDonePanel || i < stepIndex || done;
                const className = [
                  styles.stepTab,
                  active ? styles.stepTabActive : '',
                  done ? styles.stepTabDone : '',
                  canGo ? styles.stepTabNav : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const label = `${i + 1}. ${s.label}`;
                if (canGo) {
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-wp-proximity-item=""
                      className={className}
                      aria-current={active ? 'step' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOnDonePanel(false);
                        setStepIndex(i);
                      }}
                    >
                      {label}
                    </button>
                  );
                }
                return (
                  <span
                    key={s.id}
                    data-wp-proximity-item=""
                    className={className}
                    aria-current={active ? 'step' : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </ProximityScope>
          ) : null}
        </div>

        <div className={dockStyles.expandSlot} aria-hidden={!expanded}>
          <div className={dockStyles.expandInner} {...(!expanded ? { inert: true } : {})}>
            <div
              id="launch-wizard-drawer"
              className={dockStyles.body}
              data-tour="launch-wizard-drawer"
            >
              <StepTravel index={onDonePanel ? STEPS.length : stepIndex} fill>
                {onDonePanel ? (
                  <section className={styles.stepBlock}>
                    <h2 className={styles.stepTitle}>Prêt</h2>
                    <Text size="sm" c="dimmed">
                      {ready
                        ? 'Les contrôles bloquants sont verts. Prévisualisez la vitrine.'
                        : 'Certaines étapes optionnelles restent incomplètes — la boutique peut ouvrir.'}
                    </Text>
                    <div className={styles.footerEnd} style={{ marginTop: '1rem', gap: '0.5rem', display: 'flex', flexWrap: 'wrap' }}>
                      <Button
                        type="button"
                        variant="ghost"
                        tabIndex={expanded ? undefined : -1}
                        onClick={() => window.open(homePreviewUrl('/'), '_blank')}
                      >
                        Accueil marketing
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        tabIndex={expanded ? undefined : -1}
                        onClick={() => window.open(shopPreviewUrl('/'), '_blank')}
                      >
                        Boutique
                      </Button>
                    </div>
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'legal' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Remplacez les placeholders légaux (ICE, RC, …) puis enregistrez.
                      {currentCheck?.detail ? (
                        <>
                          {' '}
                          <span className={styles.headerStepHint}>{currentCheck.detail}</span>
                        </>
                      ) : null}
                    </p>
                    {error ? <InlineError>{error}</InlineError> : null}
                    <StorefrontSiteSettingsPage embedded onSaved={() => void reload()} />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'shipping' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Activez au moins une méthode de livraison.
                      {currentCheck?.detail ? ` (${currentCheck.detail})` : null}
                    </p>
                    <FulfillmentSettingsPage
                      mode="shipping"
                      embedded
                      onChanged={() => void reload()}
                    />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'payment' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Activez au moins un mode de paiement.
                      {currentCheck?.detail ? ` (${currentCheck.detail})` : null}
                    </p>
                    <FulfillmentSettingsPage
                      mode="payment"
                      embedded
                      onChanged={() => void reload()}
                    />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'featured' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Publiez au moins un produit tagué <code>storefront:featured</code>.
                    </p>
                    <StorefrontFeaturedProductsPanel onChanged={() => void reload()} />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'flows' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Parcours vitrine assurés — questionnaire et analyse photo.
                      {currentCheck?.detail ? ` (${currentCheck.detail})` : null}
                    </p>
                    <LaunchFlowsStep onChanged={() => void reload()} />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'campaigns' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Optionnel — créez des campagnes catalogue si besoin.
                      {currentCheck?.detail ? ` (${currentCheck.detail})` : null}
                    </p>
                    <CatalogCampaignsTab />
                  </section>
                ) : null}

                {!onDonePanel && currentStep?.id === 'coupons' ? (
                  <section className={styles.stepBlock}>
                    <p className={styles.stepLede}>
                      Optionnel — codes promo actifs si promotions prévues.
                      {currentCheck?.detail ? ` (${currentCheck.detail})` : null}
                    </p>
                    <DiscountCodesPage embedded onChanged={() => void reload()} />
                  </section>
                ) : null}
              </StepTravel>
            </div>

            <div className={dockStyles.footer}>
              <div className={`${dockStyles.footerInner} ${styles.footerRow}`}>
                {onDonePanel || stepIndex > 0 ? (
                  <Button
                    type="button"
                    variant="soft"
                    tabIndex={expanded ? undefined : -1}
                    onClick={() => {
                      if (onDonePanel) {
                        setOnDonePanel(false);
                        setStepIndex(STEPS.length - 1);
                        return;
                      }
                      setStepIndex((i) => Math.max(0, i - 1));
                    }}
                  >
                    Retour
                  </Button>
                ) : (
                  <span />
                )}
                <div className={styles.footerEnd}>
                  {onDonePanel ? (
                    <Button
                      type="button"
                      variant="primary"
                      tabIndex={expanded ? undefined : -1}
                      onClick={dismiss}
                    >
                      Terminer
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      tabIndex={expanded ? undefined : -1}
                      disabled={loading || !canContinueStep}
                      onClick={() => goContinue()}
                    >
                      Continuer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );

  return createPortal(sheet, document.body);
}
